import { Button, cn, Dialog, DialogContent, DialogDescription, DialogFooter, DialogTitle } from "@tellescope/ui";
import { ComponentProps, ReactNode } from "react";
import { useTranslation } from "react-i18next";

export interface TableConfigDialogProps extends ComponentProps<typeof Dialog> {
  title?: string;
  description?: string;
  children?: ReactNode;
  isDirty?: boolean;
  onApply: () => void;
  onClearAll?: () => void;
}

export const TableConfigDialog = ({
  title,
  description,
  children,
  isDirty = false,
  onApply,
  onClearAll,
  ...rest
}: TableConfigDialogProps) => {
  const { t } = useTranslation();

  const handleApply = () => {
    onApply();
    rest.onOpenChange?.(false);
  };
  const handleCancel = () => rest.onOpenChange?.(false);

  return (
    <Dialog {...rest}>
      <DialogContent className="min-w-[700px] max-h-[90vh] flex flex-col p-0 overflow-hidden gap-1">
        <div className="p-4 space-y-2">
          <DialogTitle>{title ?? t("Configure Table View")}</DialogTitle>
          <DialogDescription>
            {description ?? t("Add and remove columns, and apply filters to narrow down the data")}
          </DialogDescription>
        </div>
        <div className={cn("flex-1 overflow-y-auto space-y-6 p-4", !isDirty && "mb-2")}>{children}</div>
        {isDirty && (
          <DialogFooter className="border-t p-4 bg-gray-100 justify-end">
            {onClearAll && (
              <Button variant="outline" onClick={onClearAll} data-testid="table-config-clear-all-button">
                {t("Clear All")}
              </Button>
            )}
            <Button variant="outline" onClick={handleCancel} data-testid="table-config-cancel-button">
              {t("Cancel")}
            </Button>
            <Button onClick={handleApply} data-testid="table-config-apply-button">
              {t("Apply Filters")}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
